/**
 * Login Component
 * 
 * This component handles user authentication through Google OAuth login.
 * Streamlined for simplicity and better user experience.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../firebase/AuthContext';
import './Auth.css';

/**
 * Login Component
 * 
 * @returns {JSX.Element} Login component with Google authentication
 */
function Login() {
  // State for loading and error handling
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  
  // Get auth functions from context
  const { signInWithGoogle, error, setError } = useAuth();
  const navigate = useNavigate();
  
  /**
   * Handle Google sign-in button click
   */
  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setLocalError('');
      setError('');
      
      // Attempt Google login
      await signInWithGoogle();
      
      // Navigate to home page after successful login
      navigate('/');
    } catch (err) {
      // Handle Google sign-in errors
      if (err.code === 'auth/popup-closed-by-user') {
        setLocalError('Sign-in cancelled. Please try again.');
      } else {
        setLocalError('Failed to sign in with Google. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2 className="auth-title">Create an account </h2>
        <p className="auth-description">
          Use your Google account to sign in and track your progress in the English Acquaintances Game.
        </p>
        
        {/* Show error message if any */}
        {(localError || error) && (
          <div className="auth-error">
            {localError || error}
          </div>
        )}
        
        {/* Google sign-in button */}
        <button 
          className="google-button"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          <img 
            src="/google-icon.svg" 
            alt="Google" 
            className="google-icon" 
          />
          {loading ? 'Signing in...' : 'Sign in with Google'}
        </button>
        
        <div className="auth-benefits">
          <h3 className="benefits-title">Benefits of signing in:</h3>
          <ul className="benefits-list">
            <li>Save your progress across devices</li>
            <li>Compete with other students from your college</li>
            <li>Track your mastery of literary works</li>
            <li>Appear on the leaderboard</li>
          </ul>
        </div>
        
 
      </div>
    </div>
  );
}

export default Login; 